/**
 * The call's INTENT, and nothing else.
 *
 * Same shape and the same reason as `apps/provider/src/app/overlays/store.ts`:
 * a module-level store is the right home for "the user asked for this to be
 * open", because that outlives any one component and every route needs to be
 * able to read it. It is the wrong home for anything the session owns.
 *
 * WHAT MAY NOT GO IN HERE, ever: the session, the device controller, the
 * observers, the attendee list, the connection status, the waiting list. All of
 * those are live state whose lifetime is the session's, and a module-level
 * store holding one of them keeps a torn-down session reachable — the object
 * survives the teardown that made it useless, and the next reader gets a dead
 * handle with no way to tell. Those come from the SDK's observers and from a
 * subscription, through React context, and they die with the provider.
 *
 * ONE ACTIVE ROOM, not a stack — the one genuine difference from the overlay
 * store it is otherwise modelled on. Two live sessions fight over the
 * microphone, and there is no clean recovery from that: whichever one loses
 * sits there looking connected with nothing to send. So `enter` REPLACES.
 */
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store-react";
import type { RoomKey } from "../room";

/** The role the local person holds in the active room. Mirrors the port's. */
export type CallRole = "host" | "guest";

/**
 * The call the user has asked for. `minimised` is intent too — it is the
 * user's choice of chrome, not a fact about the session, which is exactly why
 * it survives a route change and belongs here rather than in a component.
 */
export interface ActiveCall {
  readonly key: RoomKey;
  readonly role: CallRole;
  readonly minimised: boolean;
  /**
   * Whether the waiting room column is slid open. Intent for the same reason
   * `minimised` is: it is the clinician's choice of chrome and should survive
   * a route change. Meaningless on a surface with no waiting room, which is
   * why nothing here asks whether there is one.
   */
  readonly asideOpen: boolean;
}

export const callStore = createStore({
  context: { active: undefined as ActiveCall | undefined },
  on: {
    /**
     * Enter a room. Idempotent for the room already active — re-entering does
     * NOT reset `minimised`, because the provider re-announces itself on every
     * mount and a naive reset would pop a minimised call back open whenever
     * its host remounted.
     */
    /**
     * `minimised` is an ENTRY state, not a default overridden afterwards. A
     * caller ringing out belongs in the corner card, and minimising in a second
     * send would put the full surface on screen for a frame first — a flash of
     * an empty room over whatever they were reading.
     */
    enter: (ctx, event: { key: RoomKey; role: CallRole; minimised?: boolean }) =>
      ctx.active?.key === event.key && ctx.active.role === event.role
        ? ctx
        : { active: { key: event.key, role: event.role, minimised: event.minimised ?? false, asideOpen: true } },

    minimise: (ctx) => (ctx.active ? { active: { ...ctx.active, minimised: true } } : ctx),
    expand: (ctx) => (ctx.active ? { active: { ...ctx.active, minimised: false } } : ctx),

    /**
     * Open by default on entry, because a clinician who cannot see the door has
     * no way to learn somebody is at it — the minimised chip is the only other
     * signal and it is not on screen while expanded.
     */
    toggleAside: (ctx) => (ctx.active ? { active: { ...ctx.active, asideOpen: !ctx.active.asideOpen } } : ctx),

    /**
     * Keyed, unlike the two above. A provider unmounting tells the store it has
     * gone, and an unkeyed `leave` would let a component tearing down for the
     * OLD room clear the call the user has already moved to — React runs the
     * outgoing cleanup after the incoming mount.
     */
    leave: (ctx, event: { key: RoomKey }) => (ctx.active?.key === event.key ? { active: undefined } : ctx),
  },
});

export const enterCall = (key: RoomKey, role: CallRole, options: { minimised?: boolean } = {}) =>
  callStore.send({ type: "enter", key, role, minimised: options.minimised });
export const minimiseCall = () => callStore.send({ type: "minimise" });
export const expandCall = () => callStore.send({ type: "expand" });
export const toggleCallAside = () => callStore.send({ type: "toggleAside" });
export const leaveCall = (key: RoomKey) => callStore.send({ type: "leave", key });

/** The active call, or `undefined`. The one read a shell needs. */
export const useActiveCall = (): ActiveCall | undefined => useSelector(callStore, (s) => s.context.active);
